/**
 * Derived Study Name — see GLOSSARY.md → Trial Authoring → Derived Study Name.
 *
 * Pure FR-003a derivation: the system-produced study name from the first
 * uploaded document's filename. The Python content-derivation helper
 * (`derive_study_title`) mirrors these strip rules; both are bench-verified
 * against the same fixture (research R7) so the rules cannot drift silently.
 */

/** The single fixed placeholder label (FR-003a) — never a partial guess. */
export const PLACEHOLDER_STUDY_NAME = 'Untitled study';

/**
 * Name Source — see GLOSSARY.md → Trial Authoring → Name Source. Provenance
 * of a Trial's current name; internal (never on the wire, R4). A `user` name
 * is never overwritten by the system (I-SNS-01).
 */
export type NameSource = 'filename' | 'content' | 'placeholder' | 'user';

/** Derived names are clamped to this length; typed names keep factory rules. */
const DERIVED_NAME_MAX_LENGTH = 120;

/**
 * Document-type phrases removed wherever they appear (case-insensitive,
 * whole-word). Longest-first so e.g. "User Requirements Specification" is
 * consumed before the bare "Specification" / "URS" could partially match.
 */
const DOCUMENT_TYPE_PHRASES: ReadonlyArray<string> = [
  'User Requirements Specification',
  'Technical Requirements Specification',
  'Roles and Permissions Matrix',
  'Requirements Specification',
  'Randomization Addendum',
  'R&P Matrix',
  'Specification',
  'URS',
  'TRS',
];

// Phrases contain no regex metacharacters ('&' is literal in a pattern);
// escape before adding one that does. A trailing optional `s` strips the plural
// form too ("Specifications" like "Specification") — the word boundary after it
// keeps acronyms (URS/TRS) from over-matching.
const DOCUMENT_TYPE_PATTERNS: ReadonlyArray<RegExp> = DOCUMENT_TYPE_PHRASES.map(
  (phrase) => new RegExp(`(?<![A-Za-z0-9])${phrase.replace(/ /g, '\\s+')}s?(?![A-Za-z0-9])`, 'gi'),
);

/**
 * Version / draft / ISO-date tokens are stripped ONLY when trailing
 * (FR-003a): "draft" mid-name is content, "draft" at the end is noise.
 */
const TRAILING_VERSION_TOKEN =
  /(?:^|[\s\-–—.,])(?:v[.\s]?\d+(?:\.\d+)*|draft|final|\d{4}-\d{2}-\d{2})[\s\-–—.,]*$/i;

/** Separator residue left at either end once wording has been stripped. */
const EDGE_SEPARATOR_RESIDUE = /^[\s\-–—.,;:_]+|[\s\-–—.,;:_]+$/g;

const collapseSeparators = (text: string): string =>
  text.replace(/\s+/g, ' ').replace(EDGE_SEPARATOR_RESIDUE, '');

/**
 * The shared FR-003a stripping core: removes document-type wording wherever
 * it appears, then trailing version/draft/date tokens, then collapses
 * whitespace/separators. Returns '' when nothing usable remains — callers
 * decide the fallback (`deriveStudyNameFromFilename` returns null).
 */
export const stripDocumentTypeWording = (text: string): string => {
  let stripped = text.replace(/_/g, ' ');
  for (const pattern of DOCUMENT_TYPE_PATTERNS) {
    stripped = stripped.replace(pattern, ' ');
  }
  let previous: string;
  do {
    previous = stripped;
    stripped = stripped.replace(TRAILING_VERSION_TOKEN, '');
  } while (stripped !== previous);
  return collapseSeparators(stripped);
};

/** Drops the final ".ext" segment of a filename, if present. */
const dropExtension = (filename: string): string => filename.replace(/\.[A-Za-z0-9]{1,8}$/, '');

/**
 * Derive a study name from an uploaded document's filename (FR-003/FR-003a).
 * Returns the usable derived candidate clamped to 120 characters, or null
 * when nothing usable remains (caller falls back to PLACEHOLDER_STUDY_NAME).
 */
export const deriveStudyNameFromFilename = (filename: string): string | null => {
  const candidate = stripDocumentTypeWording(dropExtension(filename));
  if (candidate.length === 0) {
    return null;
  }
  return candidate.slice(0, DERIVED_NAME_MAX_LENGTH).trimEnd();
};
